import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  Bars3Icon,
  ChartBarSquareIcon,
  ChevronRightIcon,
  CircleStackIcon,
  Cog6ToothIcon,
  CpuChipIcon,
  CreditCardIcon,
  DocumentTextIcon,
  FolderIcon,
  SignalIcon,
  Squares2X2Icon,
  SwatchIcon,
  UsersIcon,
  WalletIcon,
  WrenchScrewdriverIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import './Admin.css';
import { ThemeToggle } from '../../components/ThemeToggle';
import { useTranslation } from '../../i18n';

function AdminNavGroup({
  title,
  icon,
  pathPrefixes,
  defaultOpen = false,
  children,
}: {
  title: string;
  icon: ReactNode;
  pathPrefixes: string[];
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const location = useLocation();
  const childActive = pathPrefixes.some(
    (p) =>
      location.pathname === p || location.pathname.startsWith(`${p}/`),
  );
  const [open, setOpen] = useState(defaultOpen || childActive);

  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  return (
    <div className="admin-nav-group">
      <button
        type="button"
        className={`admin-nav-group-trigger ${childActive ? 'has-active-child' : ''}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <ChevronRightIcon
          className={`admin-nav-group-chevron ${open ? 'open' : ''}`}
          aria-hidden
        />
        <span className="admin-nav-icon" aria-hidden>
          {icon}
        </span>
        {title}
      </button>
      {open ? (
        <div className="admin-nav-group-items" role="group">
          {children}
        </div>
      ) : null}
    </div>
  );
}

export function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setMobileOpen(false);
    }
    if (mobileOpen) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  return (
    <div className="admin-layout">
      {mobileOpen ? (
        <button
          type="button"
          className="admin-mobile-overlay"
          aria-label={t('admin.a11y.closeNav')}
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      <aside className={`admin-sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="admin-sidebar-header">
          <Link to="/" className="admin-logo">
            <span className="admin-logo-brand">tween</span>
            <span className="admin-logo-tld">.studio</span>
          </Link>
          <span className="admin-badge">{t('admin.badge')}</span>
        </div>

        <div className="admin-sidebar-back">
          <Link to="/" className="admin-back-to-editor">
            <ArrowLeftIcon className="admin-back-icon" aria-hidden />
            <span>{t('admin.backToEditor')}</span>
          </Link>
        </div>

        <nav className="admin-nav">
          <NavLink
            to="/admin"
            end
            className={({ isActive }) =>
              `admin-nav-item ${isActive ? 'active' : ''}`
            }
            onClick={() => setMobileOpen(false)}
          >
            <span className="admin-nav-icon" aria-hidden>
              <Squares2X2Icon />
            </span>
            {t('admin.nav.dashboard')}
          </NavLink>
          <NavLink
            to="/admin/users"
            className={({ isActive }) =>
              `admin-nav-item ${isActive ? 'active' : ''}`
            }
            onClick={() => setMobileOpen(false)}
          >
            <span className="admin-nav-icon" aria-hidden>
              <UsersIcon />
            </span>
            {t('admin.nav.users')}
          </NavLink>
          <NavLink
            to="/admin/projects"
            className={({ isActive }) =>
              `admin-nav-item ${isActive ? 'active' : ''}`
            }
            onClick={() => setMobileOpen(false)}
          >
            <span className="admin-nav-icon" aria-hidden>
              <FolderIcon />
            </span>
            {t('admin.nav.projects')}
          </NavLink>
          <NavLink
            to="/admin/activity"
            title={t('admin.nav.activityTitle')}
            className={({ isActive }) =>
              `admin-nav-item ${isActive ? 'active' : ''}`
            }
            onClick={() => setMobileOpen(false)}
          >
            <span className="admin-nav-icon" aria-hidden>
              <SignalIcon />
            </span>
            <span className="admin-nav-label-stack">
              <span className="admin-nav-title">{t('admin.nav.activity')}</span>
              <span className="admin-nav-desc">{t('admin.nav.activityDesc')}</span>
            </span>
          </NavLink>
          <NavLink
            to="/admin/storage-providers"
            className={({ isActive }) =>
              `admin-nav-item ${isActive ? 'active' : ''}`
            }
            onClick={() => setMobileOpen(false)}
          >
            <span className="admin-nav-icon" aria-hidden>
              <CircleStackIcon />
            </span>
            {t('admin.nav.storageProviders')}
          </NavLink>
          <NavLink
            to="/admin/library"
            className={({ isActive }) =>
              `admin-nav-item ${isActive ? 'active' : ''}`
            }
            onClick={() => setMobileOpen(false)}
          >
            <span className="admin-nav-icon" aria-hidden>
              <SwatchIcon />
            </span>
            {t('admin.nav.vectorPackages')}
          </NavLink>
          <NavLink
            to="/admin/social-providers"
            className={({ isActive }) =>
              `admin-nav-item ${isActive ? 'active' : ''}`
            }
            onClick={() => setMobileOpen(false)}
          >
            <span className="admin-nav-icon" aria-hidden>
              <KeyIcon />
            </span>
            {t('admin.nav.socialProviders')}
          </NavLink>
          <NavLink
            to="/admin/disclaimers"
            className={({ isActive }) =>
              `admin-nav-item ${isActive ? 'active' : ''}`
            }
            onClick={() => setMobileOpen(false)}
          >
            <span className="admin-nav-icon" aria-hidden>
              <DocumentTextIcon />
            </span>
            {t('admin.nav.disclaimers')}
          </NavLink>
          <AdminNavGroup
            title={t('admin.nav.ai')}
            icon={<CpuChipIcon />}
            pathPrefixes={['/admin/ai-providers', '/admin/ai-prompts']}
          >
            <NavLink
              to="/admin/ai-providers"
              className={({ isActive }) =>
                `admin-nav-item admin-nav-item-nested ${isActive ? 'active' : ''}`
              }
              onClick={() => setMobileOpen(false)}
            >
              <span className="admin-nav-icon" aria-hidden>
                <CpuChipIcon />
              </span>
              {t('admin.nav.providers')}
            </NavLink>
            <NavLink
              to="/admin/ai-prompts"
              className={({ isActive }) =>
                `admin-nav-item admin-nav-item-nested ${isActive ? 'active' : ''}`
              }
              onClick={() => setMobileOpen(false)}
            >
              <span className="admin-nav-icon" aria-hidden>
                <DocumentTextIcon />
              </span>
              {t('admin.nav.prompts')}
            </NavLink>
          </AdminNavGroup>
          <AdminNavGroup
            title={t('admin.nav.payment')}
            icon={<CreditCardIcon />}
            pathPrefixes={['/admin/payment-providers', '/admin/crypto-wallets']}
          >
            <NavLink
              to="/admin/payment-providers"
              className={({ isActive }) =>
                `admin-nav-item admin-nav-item-nested ${isActive ? 'active' : ''}`
              }
              onClick={() => setMobileOpen(false)}
            >
              <span className="admin-nav-icon" aria-hidden>
                <CreditCardIcon />
              </span>
              {t('admin.nav.providers')}
            </NavLink>
            <NavLink
              to="/admin/crypto-wallets"
              className={({ isActive }) =>
                `admin-nav-item admin-nav-item-nested ${isActive ? 'active' : ''}`
              }
              onClick={() => setMobileOpen(false)}
            >
              <span className="admin-nav-icon" aria-hidden>
                <WalletIcon />
              </span>
              {t('admin.nav.cryptoWallets')}
            </NavLink>
          </AdminNavGroup>

          <NavLink
            to="/admin/install"
            className={({ isActive }) =>
              `admin-nav-item ${isActive ? 'active' : ''}`
            }
            onClick={() => setMobileOpen(false)}
          >
            <span className="admin-nav-icon" aria-hidden>
              <WrenchScrewdriverIcon />
            </span>
            {t('admin.nav.install')}
          </NavLink>
          <NavLink
            to="/admin/analytics"
            className={({ isActive }) =>
              `admin-nav-item ${isActive ? 'active' : ''}`
            }
            onClick={() => setMobileOpen(false)}
          >
            <span className="admin-nav-icon" aria-hidden>
              <ChartBarSquareIcon />
            </span>
            {t('admin.nav.analytics')}
          </NavLink>
          <NavLink
            to="/admin/settings"
            className={({ isActive }) =>
              `admin-nav-item ${isActive ? 'active' : ''}`
            }
            onClick={() => setMobileOpen(false)}
          >
            <span className="admin-nav-icon" aria-hidden>
              <Cog6ToothIcon />
            </span>
            {t('admin.nav.settings')}
          </NavLink>
        </nav>

        <div className="admin-sidebar-footer">
          <ThemeToggle variant="menu" className="admin-theme-toggle" />
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-topbar">
          <button
            type="button"
            className="admin-mobile-menu-btn"
            aria-label={t('admin.a11y.openNav')}
            onClick={() => setMobileOpen(true)}
          >
            <Bars3Icon aria-hidden />
          </button>
          <div className="admin-topbar-title">{t('admin.topbarTitle')}</div>
          <div className="admin-topbar-actions">
            <ThemeToggle />
          </div>
        </header>
        <Outlet />
      </main>
    </div>
  );
}
